import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../core-components/theme/components/Buttons';
import { CodeStep } from '../../../core-components/theme/components/CodeStep';
import type { CodeProblem, CodeStatus } from '../domain/codeStatus';
import type { PasscodeChannel } from '../types/domain.types';

import { codeProblemText, codeStepStatusOf } from './codeText';
import { channelName } from './identifierText';

export interface CodeChallengeStepProps {
  readonly channel?: PasscodeChannel;
  readonly target: string;
  readonly notes?: readonly string[];
  readonly label?: string;
  readonly labelledBy?: string;
  readonly length: number;
  readonly code: string;
  readonly attempt: number;
  readonly status: CodeStatus;
  readonly resendAt: number | null;
  readonly resending: boolean;
  readonly resendProblem: CodeProblem | null;
  readonly changeLabel: string;
  readonly notice?: React.ReactNode;
  readonly onCodeChange: (code: string) => void;
  readonly onComplete: (code: string) => void;
  readonly onResend: () => void;
  readonly onChangeTarget: () => void;
}

const isTransportRefusal = (status: CodeStatus): boolean => status.kind === 'refused' && status.problem.kind === 'transport';

const RetryCheck: React.FC<{ readonly onRetry: () => void }> = ({ onRetry }) => {
  const { t } = useTranslation();
  return (
    <Button type="button" intent="tertiary" onClick={onRetry}>
      {t('common.tryAgain', 'Try again')}
    </Button>
  );
};

export const CodeChallengeStep: React.FC<CodeChallengeStepProps> = ({
  channel,
  target,
  notes,
  label,
  labelledBy,
  length,
  code,
  attempt,
  status,
  resendAt,
  resending,
  resendProblem,
  changeLabel,
  notice,
  onCodeChange,
  onComplete,
  onResend,
  onChangeTarget,
}) => {
  const { t } = useTranslation();
  return (
    <CodeStep
      channel={channel === undefined ? undefined : channelName(t, channel)}
      target={target}
      notes={notes}
      label={label}
      labelledBy={labelledBy}
      length={length}
      code={code}
      attemptKey={attempt}
      status={codeStepStatusOf(t, status)}
      resendAt={resendAt}
      resending={resending}
      sendError={resendProblem === null ? null : codeProblemText(t, resendProblem)}
      changeLabel={changeLabel}
      notice={
        <>
          {notice}
          {isTransportRefusal(status) && <RetryCheck onRetry={() => onComplete(code)} />}
        </>
      }
      onCodeChange={onCodeChange}
      onComplete={onComplete}
      onResend={onResend}
      onChangeTarget={onChangeTarget}
    />
  );
};
